/**
 * The Buying tab's areas (BUY1): the one list the area map reads, and the one rule for which of them get a bubble.
 *
 * This lives apart from the map component on purpose. The tab asks "is there anything to draw?" (to decide whether the
 * Map section and its menu chip exist), and a test that replaces the map with a stub must not be able to take that
 * rule away from the tab. The map and the tab read the SAME predicate, so a chip never jumps to an empty section.
 *
 * ⛔ An area is a name, a fixed named place to draw it on, and a count. There is no building, parcel or building
 * coordinate anywhere in this type, and nothing here ever takes one.
 */

export interface BuySearchMapArea {
  key: string;
  label: string;
  center: { lat: number; lng: number };
  count: number;
}

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * The areas that get a bubble: a count above zero and a center that is a real place, in the contract's order.
 * An area named only by buildings that have left the search arrives with `count: 0` (so a closed card still has its
 * area) and is NOT drawn: a bubble reading "Fontana: 0" is a map of a place with nothing in it.
 */
export const drawableAreas = (areas: readonly BuySearchMapArea[] | null | undefined): BuySearchMapArea[] =>
  (areas ?? []).filter(
    (a) => Boolean(a) && isFiniteNumber(a.count) && a.count > 0 && isFiniteNumber(a.center?.lat) && isFiniteNumber(a.center?.lng)
  );
